import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useDispatch, useSelector } from 'react-redux';
import { FC, createContext, useContext, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { Document, Partner } from 'types';
import documentTypes from 'constants/documentTypes';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';
import asyncActionSwal from 'utils/asyncActionSwal';

import { updateDocument } from 'modules/documents/redux/documentActions';
import { updatePartner } from 'modules/partners/redux/partnerActions';
import { setModal } from 'modules/_shared/redux/modalActions';
import UploadDocumentModal from 'modules/documents/components/Modals/UploadDocumentModal';
import { UploadDocumentModalProvider } from 'modules/documents/components/Modals/UploadDocumentModal/context/UploadDocumentModalContext';
import { useGetPartnerDocuments } from 'modules/partners/hooks/useGetPartnerDocuments';

import { PartnerDocumentsViewContextType } from '../types/PartnerDocumentsViewContextType';
import { partnerDocumentsViewInitValue } from '../constants/partnerDocumentsViewInitValue';

const PartnerDocumentsViewContext =
  createContext<PartnerDocumentsViewContextType>(partnerDocumentsViewInitValue);

export const PartnerDocumentsViewProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
  partner: Partner;
}> = ({ children, partner }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const user = useSelector((state: State) => state.user);
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const useGetPartnerDocumentsResult = useGetPartnerDocuments({
    partnerId: partner._id,
  });

  const {
    documents,
    certificateValueSharesDocuments,
    otherDocuments,
    sharesOwnershipDocuments,
  } = useGetPartnerDocumentsResult.data;

  const { isLoading: isDocumentsLoading } = useGetPartnerDocumentsResult;

  const { fetchDocuments } = useGetPartnerDocumentsResult.actions;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleUploadDocument = () => {
    dispatch(
      setModal(
        <UploadDocumentModalProvider
          partner={partner}
          onUploadDocument={fetchDocuments}
        >
          <UploadDocumentModal />
        </UploadDocumentModalProvider>
      )
    );
  };

  const removeDocument = async (documentId: string) => {
    await store.dispatch(
      updateDocument(
        {
          _id: documentId,
          category: documentTypes.DELETED,
        },
        false
      )
    );

    const filteredDocumentIds = documents
      .filter((document) => document._id !== documentId)
      .map((document) => document._id);

    await store.dispatch(
      updatePartner({
        partnerId: partner._id,
        updatePartnerData: {
          ...partner,
          documents: filteredDocumentIds || [],
          userId: user?._id,
          society: actualSociety?._id,
        },
      })
    );
  };

  const handleRemoveDocument = async (document: Document) => {
    try {
      asyncActionSwal({
        swalTitle: t('AreYouSureAboutDelDocument'),
        swalElementName: document.name,
        swalInfoText: t('ThisProcedureCantUndo'),
        swalConfirmButtonText: t('OkDelete'),
        callback: async () => {
          try {
            setIsLoading(true);

            await removeDocument(document._id);

            fetchDocuments();

            successSwal(t('DocumentDeletedSuccessfully'));
          } catch (error) {
            errorSwal(t('ErrorRemovingDocument'));
          } finally {
            setIsLoading(false);
          }
        },
      });

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <PartnerDocumentsViewContext.Provider
      value={{
        data: {
          certificateValueSharesDocuments,
          otherDocuments,
          sharesOwnershipDocuments,
          partnerId: partner._id || '',
          partnerName: partner.name || '',
          partner,
          isLoading: isDocumentsLoading || isLoading,
        },
        actions: {
          handleUploadDocument,
          handleRemoveDocument,
        },
      }}
    >
      {children}
    </PartnerDocumentsViewContext.Provider>
  );
};

export const usePartnerDocumentsView = () => {
  const context = useContext(PartnerDocumentsViewContext);
  if (!context) {
    throw new Error(
      'usePartnerDocumentsView must be used within a PartnerDocumentsViewProvider'
    );
  }
  return context;
};
